import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Popover,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import dayjs, { Dayjs } from "dayjs";
import {
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  PROFILE_FONT,
  FILTER_BORDER_DEFAULT,
} from "../../constants/layout";
import { primaryButtonSx } from "../AssetListing/assetListingShared";

const ICON_BLUE = INPUT_BORDER_FOCUS;
const RANGE_FILL = "rgba(30, 58, 95, 0.12)";

type RangePickersDayProps = PickersDayProps<Dayjs> & {
  rangeStart?: Dayjs | null;
  rangeEnd?: Dayjs | null;
};

const RangePickersDay = ({
  rangeStart,
  rangeEnd,
  day,
  outsideCurrentMonth,
  ...other
}: RangePickersDayProps) => {
  const start = rangeStart?.startOf("day");
  const end = rangeEnd?.startOf("day");
  const current = day.startOf("day");

  const hasRange = Boolean(start && end && !start.isAfter(end));
  const isStart = hasRange && start!.isSame(current, "day");
  const isEnd = hasRange && end!.isSame(current, "day");
  const isMiddle =
    hasRange && !isStart && !isEnd && current.isAfter(start!) && current.isBefore(end!);

  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={isStart || isEnd}
      sx={{
        ...(isMiddle && {
          borderRadius: 0,
          bgcolor: `${RANGE_FILL} !important`,
          color: `${DARK_NAVY} !important`,
          "&:hover": {
            bgcolor: "rgba(30, 58, 95, 0.18) !important",
          },
        }),
        ...(isStart && {
          bgcolor: `${ICON_BLUE} !important`,
          color: "#fff !important",
          borderRadius: "50%",
        }),
        ...(isEnd && {
          bgcolor: `${ICON_BLUE} !important`,
          color: "#fff !important",
          borderRadius: "50%",
        }),
      }}
    />
  );
};

const rangeDaySlotProps = (rangeStart: Dayjs | null, rangeEnd: Dayjs | null) => ({
  day: {
    rangeStart: rangeStart && rangeEnd ? rangeStart : undefined,
    rangeEnd: rangeStart && rangeEnd ? rangeEnd : undefined,
  } as RangePickersDayProps,
});

type DateRangeValue = [Dayjs | null, Dayjs | null];

const DATE_SHORTCUTS: { label: string; getValue: () => DateRangeValue }[] = [
  {
    label: "1W",
    getValue: () => [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: "1M",
    getValue: () => [dayjs().subtract(1, "month").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: "3M",
    getValue: () => [dayjs().subtract(3, "month").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: "1Y",
    getValue: () => [dayjs().subtract(1, "year").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: "1FY",
    getValue: () => {
      const now = dayjs();
      const fyYear = now.month() >= 3 ? now.year() : now.year() - 1;
      return [dayjs().year(fyYear).month(3).date(1).startOf("day"), dayjs().endOf("day")];
    },
  },
];

const formatDisplayDate = (timestamp?: number) =>
  timestamp ? dayjs(timestamp).format("MMM D, YYYY") : "";

const toTimestampRange = (range: DateRangeValue) => ({
  startDate: range[0]!.startOf("day").valueOf(),
  endDate: range[1]!.endOf("day").valueOf(),
});

const shortcutButtonSx = (active: boolean) => ({
  fontFamily: PROFILE_FONT,
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none" as const,
  minWidth: 40,
  px: 1.25,
  py: 0.65,
  borderRadius: "20px",
  color: active ? ICON_BLUE : DARK_NAVY,
  border: `1px solid ${active ? ICON_BLUE : FILTER_BORDER_DEFAULT}`,
  bgcolor: active ? "#F5F8FC" : "#fff",
  "&:hover": {
    bgcolor: "#F5F8FC",
    borderColor: ICON_BLUE,
    color: ICON_BLUE,
  },
});

const navSelectSx = {
  fontFamily: PROFILE_FONT,
  fontSize: 14,
  fontWeight: 600,
  color: DARK_NAVY,
  bgcolor: "#fff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: FILTER_BORDER_DEFAULT,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#b8c9dc",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ICON_BLUE,
    borderWidth: "1.5px",
  },
  "& .MuiSelect-select": {
    py: 0.75,
    px: 1.25,
  },
};

const withMonthYear = (base: Dayjs, patch: { month?: number; year?: number }) => {
  let next = base.date(1);
  if (patch.year !== undefined) next = next.year(patch.year);
  if (patch.month !== undefined) next = next.month(patch.month);
  const day = Math.min(base.date(), next.daysInMonth());
  next = next.date(day);
  return next.isAfter(dayjs(), "day") ? dayjs() : next;
};

const baseCalendarSx = {
  width: 280,
  maxHeight: 320,
  "& .MuiDayCalendar-weekDayLabel": {
    fontFamily: PROFILE_FONT,
    fontWeight: 600,
    color: DARK_NAVY_LIGHT,
  },
  "& .MuiPickersDay-root": {
    fontFamily: PROFILE_FONT,
    fontWeight: 500,
    color: DARK_NAVY,
  },
  "& .MuiPickersDay-root.Mui-selected": {
    bgcolor: `${ICON_BLUE} !important`,
    color: "#fff !important",
  },
  "& .MuiPickersDay-root.Mui-selected:hover": {
    bgcolor: `${ICON_BLUE} !important`,
  },
  "& .MuiPickersDay-today": {
    borderColor: `${ICON_BLUE} !important`,
  },
};

const dropdownCalendarSx = {
  ...baseCalendarSx,
  "& .MuiPickersCalendarHeader-root": {
    display: "none",
  },
};

export type ReportDateRangePickerProps = {
  startDate?: number;
  endDate?: number;
  onChange: (range: { startDate?: number; endDate?: number }) => void;
  variant?: "default" | "compact";
  defaultShortcut?: string | null;
};

export const ReportDateRangePicker = ({
  startDate,
  endDate,
  onChange,
  variant = "default",
  defaultShortcut = null,
}: ReportDateRangePickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftStart, setDraftStart] = useState<Dayjs | null>(() =>
    startDate ? dayjs(startDate).startOf("day") : null
  );
  const [draftEnd, setDraftEnd] = useState<Dayjs | null>(() =>
    endDate ? dayjs(endDate).endOf("day") : null
  );
  const [fromRefMonth, setFromRefMonth] = useState<Dayjs>(() =>
    startDate ? dayjs(startDate).startOf("day") : dayjs().startOf("day")
  );
  const [toRefMonth, setToRefMonth] = useState<Dayjs>(() =>
    endDate ? dayjs(endDate).startOf("day") : dayjs().startOf("day")
  );
  const [activeShortcut, setActiveShortcut] = useState<string | null>(defaultShortcut);

  const open = Boolean(anchorEl);
  const today = dayjs();

  const displayLabel = useMemo(() => {
    if (startDate && endDate) {
      return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
    }
    return "Select date range";
  }, [startDate, endDate]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    const start = startDate ? dayjs(startDate).startOf("day") : null;
    const end = endDate ? dayjs(endDate).endOf("day") : null;
    setDraftStart(start);
    setDraftEnd(end);
    setFromRefMonth(start ?? dayjs().startOf("day"));
    setToRefMonth(end ?? dayjs().startOf("day"));
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const applyRange = (range: DateRangeValue, shortcut?: string) => {
    if (!range[0] || !range[1]) return;
    onChange(toTimestampRange(range));
    setActiveShortcut(shortcut ?? null);
    handleClose();
  };

  const handleShortcut = (shortcut: (typeof DATE_SHORTCUTS)[number]) => {
    const range = shortcut.getValue();
    setDraftStart(range[0]!);
    setDraftEnd(range[1]!);
    setFromRefMonth(range[0]!);
    setToRefMonth(range[1]!);
    applyRange(range, shortcut.label);
  };

  const handleApply = () => {
    if (!draftStart || !draftEnd || draftStart.isAfter(draftEnd)) return;
    applyRange([draftStart.startOf("day"), draftEnd.endOf("day")]);
    setActiveShortcut(null);
  };

  const handleReset = () => {
    setDraftStart(null);
    setDraftEnd(null);
    const now = dayjs().startOf("day");
    setFromRefMonth(now);
    setToRefMonth(now);
    setActiveShortcut(null);
    onChange({ startDate: undefined, endDate: undefined });
    handleClose();
  };

  const canApply = Boolean(draftStart && draftEnd && !draftStart.isAfter(draftEnd));

  const fromCalendarValue =
    draftStart && draftStart.isSame(fromRefMonth, "month") ? draftStart : null;
  const toCalendarValue =
    draftEnd && draftEnd.isSame(toRefMonth, "month") ? draftEnd : null;

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        value: index,
        label: dayjs().month(index).format("MMMM"),
      })),
    []
  );

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - 1999 }, (_, index) => currentYear - index);
  }, []);

  const updateFromView = (patch: { month?: number; year?: number }) => {
    setFromRefMonth(withMonthYear(fromRefMonth, patch).startOf("day"));
    setActiveShortcut(null);
  };

  const updateToView = (patch: { month?: number; year?: number }) => {
    setToRefMonth(withMonthYear(toRefMonth, patch).startOf("day"));
    setActiveShortcut(null);
  };

  const renderMonthYearSelectors = (
    label: string,
    month: Dayjs,
    onUpdate: (patch: { month?: number; year?: number }) => void
  ) => (
    <Box sx={{ mb: 0.75 }}>
      <Typography
        sx={{
          fontFamily: PROFILE_FONT,
          fontSize: 12,
          fontWeight: 600,
          color: DARK_NAVY_LIGHT,
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={0.75}>
        <Select
          size="small"
          value={month.month()}
          onChange={(event) => onUpdate({ month: Number(event.target.value) })}
          sx={{ ...navSelectSx, minWidth: 118 }}
          MenuProps={{ sx: { zIndex: 1700 } }}
        >
          {monthOptions.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={{ fontFamily: PROFILE_FONT }}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        <Select
          size="small"
          value={month.year()}
          onChange={(event) => onUpdate({ year: Number(event.target.value) })}
          sx={{ ...navSelectSx, minWidth: 88 }}
          MenuProps={{ sx: { zIndex: 1700 } }}
        >
          {yearOptions.map((year) => (
            <MenuItem key={year} value={year} sx={{ fontFamily: PROFILE_FONT }}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </Box>
  );

  const renderShortcutButtons = (singleRow = false) => (
    <Stack
      direction="row"
      spacing={0.75}
      flexWrap={singleRow ? "nowrap" : "wrap"}
      useFlexGap
      sx={singleRow ? { width: "max-content", maxWidth: "100%" } : undefined}
    >
      {DATE_SHORTCUTS.map((shortcut) => (
        <Button
          key={shortcut.label}
          size="small"
          onClick={() => handleShortcut(shortcut)}
          sx={shortcutButtonSx(activeShortcut === shortcut.label)}
        >
          {shortcut.label}
        </Button>
      ))}
    </Stack>
  );

  return (
    <Box
      sx={
        variant === "compact"
          ? {
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "stretch", sm: "flex-end" },
              gap: 1.25,
              width: { xs: "100%", sm: "auto" },
            }
          : {
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1.4fr 1fr" },
              gap: { xs: 2, sm: 2.5 },
              alignItems: "end",
            }
      }
    >
      <Box sx={{ width: "100%" }}>
        {variant === "default" ? (
          <Typography
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: 14,
              fontWeight: 600,
              color: DARK_NAVY,
              mb: 1,
            }}
          >
            Date Range
          </Typography>
        ) : (
          <Typography
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: 13,
              fontWeight: 600,
              color: DARK_NAVY,
              mb: 1,
              textAlign: { xs: "left", sm: "right" },
            }}
          >
            Quick Range
          </Typography>
        )}
        {variant === "compact" ? (
          <Box sx={{ mb: 1.25, display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
            {renderShortcutButtons(true)}
          </Box>
        ) : null}
        {variant === "compact" ? (
          <Typography
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: 13,
              fontWeight: 600,
              color: DARK_NAVY,
              mb: 1,
              textAlign: { xs: "left", sm: "right" },
            }}
          >
            Date Range
          </Typography>
        ) : null}
        <Box
          onClick={handleOpen}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleOpen(e as unknown as React.MouseEvent<HTMLElement>);
            }
          }}
          sx={{
            width: "100%",
            minWidth: { sm: variant === "compact" ? 300 : undefined },
            minHeight: 44,
            px: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            cursor: "pointer",
            borderRadius: "10px",
            bgcolor: "#fff",
            border: `1.5px solid ${FILTER_BORDER_DEFAULT}`,
            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              borderColor: "#b8c9dc",
            },
            "&:focus-visible": {
              outline: "none",
              borderColor: ICON_BLUE,
              boxShadow: "0 0 0 3px rgba(30, 58, 95, 0.12)",
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: 14,
              color: startDate && endDate ? DARK_NAVY : DARK_NAVY_LIGHT,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayLabel}
          </Typography>
          <CalendarMonthOutlinedIcon sx={{ fontSize: 20, color: DARK_NAVY_LIGHT, flexShrink: 0 }} />
        </Box>
      </Box>

      {variant === "default" ? (
        <Box>
          <Typography
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: 14,
              fontWeight: 600,
              color: DARK_NAVY,
              mb: 1,
            }}
          >
            Quick Range
          </Typography>
          {renderShortcutButtons()}
        </Box>
      ) : null}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
            sx: {
              mt: 0.75,
              borderRadius: "12px",
              minWidth: { xs: "min(100vw - 32px, 340px)", sm: 620 },
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            },
          },
        }}
        sx={{ zIndex: 1700 }}
      >
        {variant === "compact" ? (
          <Box
            sx={{
              px: 2,
              pt: 1.75,
              pb: 1,
              borderBottom: `1px solid ${FILTER_BORDER_DEFAULT}`,
              bgcolor: "#fff",
            }}
          >
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 12,
                fontWeight: 600,
                color: DARK_NAVY_LIGHT,
                mb: 1,
              }}
            >
              Quick Range
            </Typography>
            {renderShortcutButtons(true)}
          </Box>
        ) : null}

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box
            sx={{
              p: 1.5,
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 2, sm: 1.5 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 260 }}>
              {renderMonthYearSelectors("From", fromRefMonth, updateFromView)}
              <Box>
                <DateCalendar
                  key={`from-${fromRefMonth.format("YYYY-MM")}`}
                  value={fromCalendarValue}
                  referenceDate={fromRefMonth}
                  onChange={(value) => {
                    if (!value) return;
                    const next = value.startOf("day");
                    setDraftStart(next);
                    if (draftEnd && next.isAfter(draftEnd)) setDraftEnd(next.endOf("day"));
                    setFromRefMonth(next);
                    setActiveShortcut(null);
                  }}
                  onMonthChange={(month) => setFromRefMonth(month)}
                  maxDate={today}
                  slots={{ day: RangePickersDay }}
                  slotProps={rangeDaySlotProps(draftStart, draftEnd)}
                  sx={dropdownCalendarSx}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: { xs: "none", sm: "block" },
                width: "1px",
                alignSelf: "stretch",
                bgcolor: FILTER_BORDER_DEFAULT,
              }}
            />

            <Box sx={{ flex: 1, minWidth: 260 }}>
              {renderMonthYearSelectors("To", toRefMonth, updateToView)}
              <Box>
                <DateCalendar
                  key={`to-${toRefMonth.format("YYYY-MM")}`}
                  value={toCalendarValue}
                  referenceDate={toRefMonth}
                  onChange={(value) => {
                    if (!value) return;
                    const next = value.endOf("day");
                    setDraftEnd(next);
                    if (draftStart && next.isBefore(draftStart)) setDraftStart(next.startOf("day"));
                    setToRefMonth(next.startOf("day"));
                    setActiveShortcut(null);
                  }}
                  onMonthChange={(month) => setToRefMonth(month)}
                  minDate={draftStart ?? undefined}
                  maxDate={today}
                  slots={{ day: RangePickersDay }}
                  slotProps={rangeDaySlotProps(draftStart, draftEnd)}
                  sx={dropdownCalendarSx}
                />
              </Box>
            </Box>
          </Box>
        </LocalizationProvider>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${FILTER_BORDER_DEFAULT}`,
            bgcolor: "#fff",
          }}
        >
          <Button
            onClick={handleReset}
            sx={{
              fontFamily: PROFILE_FONT,
              textTransform: "none",
              color: DARK_NAVY_LIGHT,
              fontWeight: 500,
            }}
          >
            Reset
          </Button>
          <Button onClick={handleApply} disabled={!canApply} sx={{ ...primaryButtonSx, px: 2.5 }}>
            Apply
          </Button>
        </Stack>
      </Popover>
    </Box>
  );
};
